import { getMomentTz } from "@dexteel/mesf-core";
import {
	createMeterNameResolver,
	hasMultipleAccounts,
	UTILITY_CONSUMPTION_CHART_METRIC,
	UTILITY_CONSUMPTION_MODE,
	type UtilityConsumptionChartMetric,
	type UtilityConsumptionMainBucket,
	type UtilityConsumptionMeter,
	type UtilityConsumptionMode,
} from "../../models/UtilityConsumption";
import {
	formatVolume,
	NO_DATA_TEXT,
	RATIO_UNIT,
	VOLUME_UNIT,
} from "../../utils/format";
import type { ConsumptionDetailRow } from "./ConsumptionDetailGrid";

const moment = getMomentTz();

const METRIC_LABELS: Record<
	UtilityConsumptionChartMetric,
	{ unit: string; axisName: string }
> = {
	[UTILITY_CONSUMPTION_CHART_METRIC.GALLONS]: {
		unit: VOLUME_UNIT,
		axisName: "Gallons",
	},
	[UTILITY_CONSUMPTION_CHART_METRIC.GALLONS_PER_TON]: {
		unit: RATIO_UNIT,
		axisName: "Gallons per ton",
	},
};

export const getBucketLabels = (
	starts: Date[],
	mode: UtilityConsumptionMode,
) => {
	if (mode !== UTILITY_CONSUMPTION_MODE.DATE) {
		return starts.map((start) => {
			const local = moment(start);
			return mode === UTILITY_CONSUMPTION_MODE.MONTH
				? local.format("MMM")
				: local.format("M/D");
		});
	}

	const localTimes = starts.map((start) => moment(start).format("HH:mm"));
	return starts.map((start, index) => {
		const localTime = localTimes[index];
		const repeated =
			localTimes.indexOf(localTime) !== localTimes.lastIndexOf(localTime);
		return repeated ? `${localTime} ${moment(start).format("Z")}` : localTime;
	});
};

export interface ConsumptionChartModel {
	labels: string[];
	meterIds: string[];
	unit: string;
	axisName: string;
	valuesByMeterId: Map<string, (number | null)[]>;
	showAccountNumbers: boolean;
	accountNumberByMeterId: Map<string, string>;
	getMeterName: (meterId: string) => string;
	getMeterColor: (meterId: string) => string;
	getSeriesName: (meterId: string) => string;
	meterIdBySeriesName: Map<string, string>;
	detailRows: ConsumptionDetailRow[];
	bucketIndexByRowId: Map<string, number>;
	groupLabel?: string;
	getStackKey?: (meterId: string) => string;
	showTooltipTotal?: boolean;
}

interface BuildConsumptionChartModelInput {
	buckets: UtilityConsumptionMainBucket[];
	meters: UtilityConsumptionMeter[];
	mode: UtilityConsumptionMode;
	metric: UtilityConsumptionChartMetric;
}

export const buildConsumptionChartModel = ({
	buckets,
	meters,
	mode,
	metric,
}: BuildConsumptionChartModelInput): ConsumptionChartModel => {
	const starts = [
		...new Map(
			buckets.map((bucket) => [
				bucket.BucketStartUtc.toISOString(),
				bucket.BucketStartUtc,
			]),
		).values(),
	];
	const bucketMeterIds = new Set(
		buckets.map((bucket) => bucket.ExternalMeterId),
	);

	const meterIds = [
		...meters
			.map((meter) => meter.ExternalMeterId)
			.filter((meterId) => bucketMeterIds.has(meterId)),
		...[...bucketMeterIds].filter(
			(meterId) => !meters.some((meter) => meter.ExternalMeterId === meterId),
		),
	];
	const showAccountNumbers = hasMultipleAccounts(meters);
	// A meter with no account has nothing to prefix, so it keeps its bare ID.
	const accountNumberByMeterId = new Map(
		meters
			.filter((meter) => meter.AccountNumber !== null)
			.map((meter) => [meter.ExternalMeterId, meter.AccountNumber as string]),
	);
	const getMeterName = createMeterNameResolver(meters);
	const colorByMeterId = new Map(
		meters.map((meter) => [meter.ExternalMeterId, meter.Color]),
	);
	const getMeterColor = (meterId: string) =>
		colorByMeterId.get(meterId) ?? "#8C9AA5";
	const getSeriesName = (meterId: string) => {
		const accountNumber = accountNumberByMeterId.get(meterId);
		return showAccountNumbers && accountNumber
			? `Account: ${accountNumber} · ${getMeterName(meterId)}`
			: getMeterName(meterId);
	};
	const bucketByStartAndMeter = new Map(
		buckets.map((bucket) => [
			`${bucket.BucketStartUtc.toISOString()}|${bucket.ExternalMeterId}`,
			bucket,
		]),
	);
	const valueFor = (start: Date, meterId: string) => {
		const bucket = bucketByStartAndMeter.get(
			`${start.toISOString()}|${meterId}`,
		);
		if (bucket === undefined) return null;
		return metric === UTILITY_CONSUMPTION_CHART_METRIC.GALLONS_PER_TON
			? bucket.GallonsPerTon
			: bucket.Gallons;
	};
	const labels = getBucketLabels(starts, mode);

	return {
		labels,
		meterIds,
		unit: METRIC_LABELS[metric].unit,
		axisName: METRIC_LABELS[metric].axisName,
		valuesByMeterId: new Map(
			meterIds.map((meterId) => [
				meterId,
				starts.map((start) => valueFor(start, meterId)),
			]),
		),
		showAccountNumbers,
		accountNumberByMeterId,
		getMeterName,
		getMeterColor,
		getSeriesName,
		meterIdBySeriesName: new Map(
			meterIds.map((meterId) => [getSeriesName(meterId), meterId]),
		),
		detailRows: starts.map((start, index) => {
			const bucketValues = meterIds
				.map((meterId) => valueFor(start, meterId))
				.filter((value): value is number => value !== null);
			return {
				id: start.toISOString(),
				period: labels[index] ?? NO_DATA_TEXT,
				value:
					formatVolume(
						bucketValues.length === 0
							? null
							: bucketValues.reduce((total, value) => total + value, 0),
					) ?? NO_DATA_TEXT,
			};
		}),
		bucketIndexByRowId: new Map(
			starts.map((start, index) => [start.toISOString(), index]),
		),
	};
};
